import EnIcon from '@assets/languages/en.svg?react'
import EsIcon from '@assets/languages/es.svg?react'
import HrIcon from '@assets/languages/hr.svg?react'
import JaIcon from '@assets/languages/ja.svg?react'
import PlIcon from '@assets/languages/pl.svg?react'
import PtIcon from '@assets/languages/pt.svg?react'
import { DropdownChevron } from '@components'
import settings from '@config/settings'
import cn from '@integrations/classnames'
import { useEffect } from '@integrations/react'
import { useDispatch } from '@integrations/state'
import { normalizeLanguageCode } from '@utils/language'
import {
  changeLanguage,
  LoginStatuses,
  selectCurrentLanguage,
  selectCurrentUserStatus,
  useAppSelector,
  useSetUserLanguageMutation
} from 'mmfintech-backend-api'
import { initializeLanguage, languages, storeLanguage, useDropDownMenu } from 'mmfintech-commons'

import surfaceStyles from '../HeaderMenuSurface/HeaderMenuSurface.module.scss'
import styles from './LanguageMenu.module.scss'

const icons = {
  en: <EnIcon />,
  es: <EsIcon />,
  ja: <JaIcon />,
  pl: <PlIcon />,
  hr: <HrIcon />,
  pt: <PtIcon />
}

interface Props {
  hamburger?: boolean
  showIcons?: boolean
  bordered?: boolean
  pill?: boolean
}

function LanguageMenu({ hamburger = false, showIcons = false, bordered = false, pill = false }: Props) {
  const userStatus = useAppSelector(selectCurrentUserStatus)
  const rawSelectedLanguage = useAppSelector(selectCurrentLanguage)
  const [setLanguage] = useSetUserLanguageMutation()

  const filteredLanguages = settings.languages
    .map((code) => languages.find((v) => v.code === code))
    .filter((v) => v != null)

  const dispatch = useDispatch()

  const { visible, hideDropDown, toggleDropDown, setRef } = useDropDownMenu()

  const selectedLanguage = normalizeLanguageCode(rawSelectedLanguage)

  useEffect(() => {
    const normalized = normalizeLanguageCode(rawSelectedLanguage)
    if (rawSelectedLanguage && normalized !== rawSelectedLanguage) {
      dispatch(changeLanguage(normalized))
      storeLanguage(normalized)
      initializeLanguage(normalized)
    }
  }, [rawSelectedLanguage, dispatch])

  function handleLanguageClick(languageCode: string): void {
    if (languageCode !== selectedLanguage) {
      if (userStatus === LoginStatuses.LOGGED_IN) {
        setLanguage({
          language: languageCode
        })
      }
      dispatch(changeLanguage(languageCode))
      storeLanguage(languageCode)
    }
    hideDropDown()
    initializeLanguage(languageCode)
  }

  function getLanguageName(): string {
    const data = filteredLanguages.find((item) => item.code === selectedLanguage)
    return data ? data.name : ''
  }

  if (!selectedLanguage) {
    return null
  }

  return (
    <div
      className={cn(surfaceStyles['header-menu-wrapper'], styles['language-menu-wrapper'], 'language-menu-wrapper', {
        [styles['hamburger']]: hamburger,
        hamburger
      })}
      data-test='language-menu-wrapper'
      ref={setRef}
    >
      <div
        className={cn(surfaceStyles['header-menu-trigger'], styles['menu-container'], 'menu-container', {
          [styles['bordered']]: bordered,
          bordered: bordered,
          [surfaceStyles['header-menu-pill']]: pill,
          'header-menu-pill': pill,
          [styles['pill']]: pill,
          pill,
          [surfaceStyles['opened']]: pill && visible
        })}
        onClick={toggleDropDown}
      >
        {showIcons ? (
          <span className={cn(styles['icon'], 'icon')}>{icons[selectedLanguage]}</span>
        ) : (
          <span className={cn(styles['icon'], 'icon', styles['additional'], 'additional')} />
        )}
        <span className={cn(surfaceStyles['header-menu-label'], styles['selected'], 'selected')}>
          {getLanguageName()}
        </span>
        <DropdownChevron className={cn(styles['arrow'], 'arrow')} expanded={visible} />
      </div>
      <div
        className={cn(
          surfaceStyles['header-menu-dropdown'],
          styles['menu-dropdown'],
          'menu-dropdown',
          'raised-frame-surface',
          'shadow-card',
          {
            [surfaceStyles['opened']]: visible,
            opened: visible
          }
        )}
        data-test='language-menu-content'
      >
        <ul className={cn(surfaceStyles['header-menu-list'], 'header-menu-list')} data-test='language-menu-list'>
          {filteredLanguages?.map((v) => (
            <li
              className={cn(surfaceStyles['header-menu-option'], styles['menu-option'], 'dropdown-option')}
              key={v.code}
              onClick={() => handleLanguageClick(v.code)}
              data-test='language-menu-item'
            >
              {showIcons && <span className={cn(styles['icon'], 'icon')}>{icons[v.code]}</span>}
              {v.name}
            </li>
          ))}
        </ul>
      </div>
    </div>
  )
}

export default LanguageMenu
